import React from 'react';
import { format } from 'date-fns';
import { Task, PRIORITY_COLORS } from '../types/Task';
import { Edit, Trash2, ExternalLink, Clock, User, Calendar, Hash, Play, CheckCircle, Image as ImageIcon } from 'lucide-react';
import { KanbanColumn } from '../services/api';
import './TaskCard.css';

interface TaskCardProps {
  task: Task;
  columns?: KanbanColumn[];
  onEdit: (task: Task) => void;
  onDelete: (id: string) => void;
  onQuickStatusChange: (taskId: string, newStatus: string) => void;
  onImagePreview?: (task: Task) => void;
  isKanbanView?: boolean;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, columns = [], onEdit, onDelete, onQuickStatusChange, onImagePreview, isKanbanView = false }) => {
  const formatDate = (dateString: string) => {
    if (!dateString || dateString === '0001-01-01T00:00:00Z') return '';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return '';
    }
  };

  const isOverdue = () => {
    // Overdue gdy:
    // 1. Jest expectedDate 
    // 2. expectedDate jest w przeszłości
    // 3. I nie ma workCompletedAt LUB workCompletedAt jest późniejsze niż expectedDate
    if (!task.expectedDate) return false;
    
    const expectedDate = new Date(task.expectedDate);
    const now = new Date();
    
    // Jeśli expectedDate nie został przekroczony, nie jest overdue
    if (expectedDate >= now) return false;
    
    // Jeśli nie ma workCompletedAt, jest overdue
    if (!task.workCompletedAt) return true;
    
    // Jeśli workCompletedAt jest późniejsze niż expectedDate, jest overdue
    const workCompletedDate = new Date(task.workCompletedAt);
    return workCompletedDate > expectedDate;
  };

  const getProgressPercentage = () => {
    if (task.originalEstimate === 0) return 0;
    return Math.min((task.actualTime / task.originalEstimate) * 100, 100);
  };

  // Create a mapping from status to column color
  const getStatusColor = (status: string): string => {
    const column = columns.find(col => col.status === status);
    return column?.color || '#646669'; // Default fallback color
  };

  // Get the display name for status from column title
  const getStatusDisplayName = (status: string): string => {
    const column = columns.find(col => col.status === status);
    return column?.title || status.replace('-', ' ');
  };

  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent task edit modal from opening
    if (task.images && task.images.length > 0 && onImagePreview) {
      onImagePreview(task);
    }
  };

  return (
    <div className={`task-card ${task.status} ${isOverdue() ? 'overdue' : ''} ${isKanbanView ? 'kanban-card' : ''}`}>
      <div className="task-header">
        <div className="task-meta">
          {/* Task ID */}
          <span className="task-id" title={`Task ID: ${task.id}`}>
            <Hash size={12} />
            {task.id.substring(0, 8)}
          </span>
          <span 
            className="priority-badge" 
            style={{ backgroundColor: PRIORITY_COLORS[task.priority] }}
          >
            {task.priority}
          </span>
          <span 
            className="status-badge"
            style={{ backgroundColor: getStatusColor(task.status) }}
          >
            {getStatusDisplayName(task.status)}
          </span>
          {task.tag && (
            <span className="tag-badge">
              {task.tag}
            </span>
          )}
        </div>
        <div className="task-actions">
          <button 
            className="action-btn edit-btn" 
            onClick={() => onEdit(task)}
            title="Edit task"
          >
            <Edit size={16} />
          </button>
          <button 
            className="action-btn delete-btn" 
            onClick={() => onDelete(task.id)}
            title="Delete task"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <div className="task-content">
        <h3 className="task-summary">{task.summary}</h3>
        {task.description && (
          <p className="task-description">{task.description}</p>
        )}

        {task.labels.length > 0 && (
          <div className="task-labels">
            {task.labels.map((label, index) => (
              <span key={index} className="label">
                {label}
              </span>
            ))}
          </div>
        )}

        {task.videoTutorialLink && (
          <div className="video-tutorial">
            <a 
              href={task.videoTutorialLink} 
              target="_blank" 
              rel="noopener noreferrer"
              className="tutorial-link"
            >
              <ExternalLink size={14} />
              <span>{task.videoTutorialTitle || 'Video Tutorial'}</span>
            </a>
          </div>
        )}

        {/* Image count indicator */}
        {task.images && task.images.length > 0 && (
          <div 
            className="image-count-indicator" 
            onClick={handleImageClick}
            title={`View ${task.images.length} attached image${task.images.length > 1 ? 's' : ''}`}
          >
            <ImageIcon size={14} />
            <span>{task.images.length}</span>
          </div>
        )}
      </div>

      <div className="task-footer">
        <div className="task-info">
          <div className="info-item">
            <User size={14} />
            <span>{task.owner || 'Unassigned'}</span>
          </div>
          {task.expectedDate && formatDate(task.expectedDate) && (
            <div className={`info-item ${isOverdue() ? 'overdue-text' : ''}`}>
              <Calendar size={14} />
              <span>{formatDate(task.expectedDate)}</span>
            </div>
          )}
          {task.workStartedAt && formatDate(task.workStartedAt) && (
            <div className="info-item work-date">
              <Play size={14} />
              <span>Started: {formatDate(task.workStartedAt)}</span>
            </div>
          )}
          {task.workCompletedAt && formatDate(task.workCompletedAt) && (
            <div className="info-item work-date">
              <CheckCircle size={14} />
              <span>Completed: {formatDate(task.workCompletedAt)}</span>
            </div>
          )}
          {task.originalEstimate > 0 && (
            <div className="info-item">
              <Clock size={14} />
              <span>{task.actualTime}h / {task.originalEstimate}h</span>
            </div>
          )}
        </div>

        {task.originalEstimate > 0 && (
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ 
                width: `${getProgressPercentage()}%`,
                backgroundColor: getProgressPercentage() > 100 ? 
                  'var(--error-color)' : 'var(--main-color)'
              }}
            />
          </div>
        )}
      </div>

      {isOverdue() && (
        <div className="overdue-indicator">
          overdue
        </div>
      )}
    </div>
  );
};

export default TaskCard; 